import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "develop-data-and-schema-patches"
    }}>{`Develop data and schema patches`}</h1>
    <p>{`A data patch is a class that contains data modification instructions. It is defined in a `}<inlineCode parentName="p">{`<Vendor>/<Module_Name>/Setup/Patch/Data/<Patch_Name>.php`}</inlineCode>{` file and implements `}<inlineCode parentName="p">{`\\Magento\\Framework\\Setup\\Patch\\DataPatchInterface`}</inlineCode>{`.`}</p>
    <p>{`A schema patch contains custom schema modification instructions. These modifications can be complex. It is defined in a `}<inlineCode parentName="p">{`<Vendor>/<Module_Name>/Setup/Patch/Schema/<Patch_Name>.php`}</inlineCode>{` file and implements `}<inlineCode parentName="p">{`\\Magento\\Framework\\Setup\\Patch\\SchemaPatchInterface`}</inlineCode>{`.`}</p>
    <p>{`Unlike the declarative schema approach, patches will only be applied once. A list of applied patches is stored in the `}<inlineCode parentName="p">{`patch_list`}</inlineCode>{` database table. An unapplied patch will be applied when running the `}<inlineCode parentName="p">{`setup:upgrade`}</inlineCode>{` from the Magento CLI.`}</p>
    <p>{`Optionally, if you plan to enable rollback for your patch during module uninstallation, then you must implement `}<inlineCode parentName="p">{`\\Magento\\Framework\\Setup\\Patch\\PatchRevertableInterface`}</inlineCode>{`.`}</p>
    <p>{`The declarative schema approach removes the version from the `}<inlineCode parentName="p">{`setup_module`}</inlineCode>{` table (in a backward compatible way), leaving only the Composer version. Therefore, you can create all new patches and modules without specifying a `}<inlineCode parentName="p">{`setup_module`}</inlineCode>{` version.`}</p>
    <p>{`The sequence of installing patches is handled through a dependency-based approach. Patches can either be independent or dependent on other patches. Independent patches can be installed in any sequence. A dependent patch requires a minimal number of patches so that it can be installed successfully.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Adobe Commerce and Magento Open Source prioritize the declarative schema approach and executes updates from the `}<a parentName="p" {...{
        "href": "configuration.md"
      }}>{`db_schema.xml`}</a>{` before the data and schema patches.`}</p>
    <p>{`To define a dependency in a patch, add the method `}<inlineCode parentName="p">{`public static function getDependencies()`}</inlineCode>{`
to the patch class and return the class names of the patches this patch depends on. The dependency can be in any module.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`public static function getDependencies()
{
    return [
        \\SomeVendor\\SomeModule\\Setup\\Patch\\Data\\SomePatch::class
    ];
}
`}</code></pre>
    <p>{`The following code sample defines a data patch class that has a dependency.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace Magento\\DummyModule\\Setup\\Patch\\Data;

use Magento\\Framework\\Setup\\ModuleDataSetupInterface;
use Magento\\Framework\\Setup\\Patch\\DataPatchInterface;
use Magento\\Framework\\Setup\\Patch\\PatchRevertableInterface;

class DummyPatch
    implements DataPatchInterface,
    PatchRevertableInterface
{
    /**
     * @var ModuleDataSetupInterface
     */
    private $moduleDataSetup;

    /**
     * @param ModuleDataSetupInterface $moduleDataSetup
     */
    public function __construct(
        ModuleDataSetupInterface $moduleDataSetup
    ) {
        /**
         * If before, we pass $setup as argument in install/upgrade function, from now we start
         * inject it with DI. If you want to use setup, you can inject it, with the same way as here
         */
        $this->moduleDataSetup = $moduleDataSetup;
    }

    /**
     * @inheritdoc
     */
    public function apply()
    {
        $this->moduleDataSetup->getConnection()->startSetup();
        //The code that you want apply in the patch
        //Please note, that one patch is responsible only for one setup version
        //So one UpgradeData can consist of few data patches
        $this->moduleDataSetup->getConnection()->endSetup();
    }

    /**
     * @inheritdoc
     */
    public static function getDependencies()
    {
        /**
         * This is dependency to another patch. Dependency should be applied first
         * One patch can have few dependencies
         * Patches do not have versions, so if in old approach with Install/Upgrade data scripts you used
         * versions, right now you need to point from patch with higher version to patch with lower version
         * But please, note, that some of your patches can be independent and can be installed in any sequence
         * So use dependencies only if this important for you
         */
        return [
            SomeDependency::class
        ];
    }

    public function revert()
    {
        $this->moduleDataSetup->getConnection()->startSetup();
        //Here should go code that will revert all operations from \`apply\` method
        //Please note, that some operations, like removing data from column, that is in role of foreign key reference
        //is dangerous, because it can trigger ON DELETE statement
        $this->moduleDataSetup->getConnection()->endSetup();
    }

    /**
     * @inheritdoc
     */
    public function getAliases()
    {
        /**
         * This internal method, that means that some patches with time can change their names,
         * but changing name should not affect installation process, that's why if we will change name of the patch
         * we will add alias here
         */
        return [];
    }
}
`}</code></pre>
    <h2 {...{
      "id": "reverting-data-patches"
    }}>{`Reverting data patches`}</h2>
    <p>{`Adobe Commerce and Magento Open Source do not allow you to revert a particular module data patch. However, you can revert all `}<inlineCode parentName="p">{`composer`}</inlineCode>{` installed or `}<inlineCode parentName="p">{`non-composer`}</inlineCode>{` installed data patches using the `}<inlineCode parentName="p">{`module:uninstall`}</inlineCode>{` command.`}</p>
    <p>{`Run the following command to revert all `}<inlineCode parentName="p">{`composer`}</inlineCode>{` installed data patches:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bin/magento module:uninstall Vendor_ModuleName
`}</code></pre>
    <p>{`Run the following command to revert all `}<inlineCode parentName="p">{`non-composer`}</inlineCode>{` installed data patches:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bin/magento module:uninstall --non-composer Vendor_ModuleName
`}</code></pre>
    <h2 {...{
      "id": "will-old-scripts-work-in-newer-versions"
    }}>{`Will old scripts work in newer versions?`}</h2>
    <p>{`Old scripts will work with new versions of Magento. However, if you want to convert your old scripts to the new format,
implement `}<inlineCode parentName="p">{`\\Magento\\Framework\\Setup\\Patch\\PatchVersionInterface`}</inlineCode>{`. This interface allows you to specify the setup version of the module in your database. If the version of the module is higher than or equal to the version specified in your patch, then the patch is skipped. If the version in the database is lower, then the patch installs.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      